import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {discoverTenantSettingsAccount} from '../redux/AuthCRUD'
import {UsersClient} from '../../../infrastracture/api/UsersClient'

const initialValues = {
  email: '',
  shortCode: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState('')

  // Login.tsx stores this once it has resolved the account, by email discovery or by short code,
  // and that step is the only one showing the "Forgot Password?" link. It's missing when this
  // page is opened directly, so the short code field is offered instead.
  const tenantAccountId = localStorage.getItem('tenantAccountId')

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      const fail = (message: string) => {
        setHasErrors(true)
        setErrorMessage(message)
      }

      try {
        let accountId = tenantAccountId

        if (!accountId && values.shortCode.trim()) {
          let discovery: {ok: boolean; data: any}

          try {
            discovery = await discoverTenantSettingsAccount(values.shortCode.trim())
          } catch (error) {
            // A short code matching no account answers 500 rather than a clean failure.
            console.error(error)
            discovery = {ok: false, data: null}
          }

          if (!discovery.ok) {
            fail('We could not find an account with that short code')
            return
          }

          accountId = discovery.data.id
        }

        const response = await UsersClient.BeginPasswordReset(values.email, accountId)
        if (response.successful) {
          setHasErrors(false)
        } else {
          fail(
            response.validationErrors?.[0]?.message ||
              response.errorMessage ||
              response.message ||
              'Something went wrong. Please try again.'
          )
        }
      } catch (error) {
        console.error(error)
        fail('Something went wrong. Please try again.')
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>Forgot Password ?</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-400 fw-bold fs-4'>Enter your email to reset your password.</div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{errorMessage}</div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info text-center'>
              If your email exists, a reset password email will be sent
            </div>
          </div>
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        {/* Kept on screen after an error so a mistyped email or short code can be retried. */}
        {hasErrors !== false && (
          <>
            <div className='fv-row mb-10'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
              <input
                type='email'
                placeholder=''
                autoComplete='off'
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {'is-invalid': formik.touched.email && formik.errors.email},
                  {
                    'is-valid': formik.touched.email && !formik.errors.email,
                  }
                )}
              />
              {formik.touched.email && formik.errors.email && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {!tenantAccountId && (
              <div className='fv-row mb-10'>
                <label className='form-label fw-bolder text-gray-900 fs-6'>
                  Company Short Code
                </label>
                <input
                  type='text'
                  placeholder='Please enter your company short code'
                  autoComplete='off'
                  {...formik.getFieldProps('shortCode')}
                  className='form-control form-control-lg form-control-solid'
                />
                <div className='text-muted fs-7 mt-2'>
                  Only needed if you enter a company short code when you sign in.
                </div>
              </div>
            )}

            {/* begin::Form group */}
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              <button
                type='submit'
                id='kt_password_reset_submit'
                className='btn btn-lg btn-primary fw-bolder me-4'
              >
                {!formik.isSubmitting && <span className='indicator-label'>Submit</span>}
                {formik.isSubmitting && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
              <Link to='/login'>
                <button
                  type='button'
                  id='kt_login_password_reset_form_cancel_button'
                  className='btn btn-lg btn-light-primary fw-bolder'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  Cancel
                </button>
              </Link>{' '}
            </div>
          </>
        )}

        {/* end::Form group */}
      </form>
    </>
  )
}
